import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  getAllJobs,
  getAllProjects,
  closeJob,
  closeProject,
  deleteJob,
  deleteProject,
  archiveJob,
  archiveProject
} from '../../services/firestoreService';
import { archiveOldClosedPostings } from '../../services/archiveService';

const PostingsDashboard = () => {
  const { currentUser, isAdmin } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('jobs');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  // Only allow admin access
  if (!currentUser || (!isAdmin() && !['admin@the-apn.com', 'cburnham@the-apn.com'].includes(currentUser.email))) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <h3 className="text-red-800 font-medium">Access Denied</h3>
          <p className="text-red-700">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  const loadData = async () => {
    try {
      setLoading(true);
      console.log('Loading dashboard data...');

      // Load jobs with error handling
      let jobsData = [];
      try {
        jobsData = await getAllJobs(100);
        console.log('Jobs loaded from Firebase:', jobsData.length);
      } catch (jobError) {
        console.error('Error loading jobs:', jobError);
        jobsData = [];
      }

      // Load projects with error handling
      let projectsData = [];
      try {
        projectsData = await getAllProjects(100);
        console.log('Projects loaded from Firebase:', projectsData.length);
      } catch (projectError) {
        console.error('Error loading projects:', projectError);
        projectsData = [];
      }

      setJobs(jobsData);
      setProjects(projectsData);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      // Set empty arrays as fallback
      setJobs([]);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, type, newStatus) => {
    try {
      if (newStatus === 'closed') {
        if (type === 'job') {
          await closeJob(id, currentUser.uid);
        } else {
          await closeProject(id, currentUser.uid);
        }
      } else if (newStatus === 'archived') {
        if (type === 'job') {
          await archiveJob(id, currentUser.uid);
        } else {
          await archiveProject(id, currentUser.uid);
        }
      }
      loadData(); // Refresh data
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status. Please try again.');
    }
  };

  const handleDelete = async (id, type, title) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to permanently delete "${title}"? This action cannot be undone.`
    );
    
    if (!confirmDelete) return;
    
    try {
      if (type === 'job') {
        await deleteJob(id);
      } else {
        await deleteProject(id);
      }
      loadData(); // Refresh data
      alert(`${type === 'job' ? 'Job' : 'Project'} deleted successfully!`);
    } catch (error) {
      console.error('Error deleting:', error);
      alert('Failed to delete. Please try again.');
    }
  };

  const runArchiveProcess = async () => {
    try {
      const result = await archiveOldClosedPostings();
      alert(`Archive process completed. Archived ${result.archivedJobs} jobs and ${result.archivedProjects} projects.`);
      loadData(); // Refresh data
    } catch (error) {
      console.error('Error running archive process:', error);
      alert('Failed to run archive process. Please try again.');
    }
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      active: 'bg-green-100 text-green-800',
      closed: 'bg-yellow-100 text-yellow-800',
      archived: 'bg-gray-100 text-gray-800'
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusStyles[status] || 'bg-gray-100 text-gray-800'}`}>
        {status || 'active'}
      </span>
    );
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    return dateObj.toLocaleDateString();
  };

  const filterData = (data) => {
    return data.filter(item => {
      const matchesStatus = statusFilter === 'all' || (item.status || 'active') === statusFilter;
      const matchesSearch = (item.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (item.company || item.client || '').toLowerCase().includes(searchTerm.toLowerCase());
      return matchesStatus && matchesSearch;
    });
  };

  const currentData = activeTab === 'jobs' ? filterData(jobs) : filterData(projects);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-apn-blue mx-auto mb-4"></div>
          <p>Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Postings Dashboard</h1>
        <p className="text-gray-600 mt-2">Manage all job and project postings</p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                  <span className="text-white font-bold">J</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Active Jobs</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {jobs.filter(j => (j.status || 'active') === 'active').length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                  <span className="text-white font-bold">P</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Active Projects</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {projects.filter(p => (p.status || 'active') === 'active').length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-yellow-500 rounded-md flex items-center justify-center">
                  <span className="text-white font-bold">C</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Closed (This Week)</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {[...jobs, ...projects].filter(item => item.status === 'closed').length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-gray-500 rounded-md flex items-center justify-center">
                  <span className="text-white font-bold">A</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Archived</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {[...jobs, ...projects].filter(item => item.status === 'archived').length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Archive Process Button */}
      <div className="mb-6">
        <button
          onClick={runArchiveProcess}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition duration-200"
        >
          Run Archive Process
        </button>
        <p className="text-sm text-gray-600 mt-1">
          Archives jobs/projects that have been closed for more than 1 week
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('jobs')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'jobs'
                ? 'border-apn-blue text-apn-blue'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Jobs ({jobs.length})
          </button>
          <button
            onClick={() => setActiveTab('projects')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'projects'
                ? 'border-apn-blue text-apn-blue'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Projects ({projects.length})
          </button>
        </nav>
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-wrap gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="all">All Statuses</option>
            <option value="active">Active</option>
            <option value="closed">Closed</option>
            <option value="archived">Archived</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by title or company..."
            className="border border-gray-300 rounded-md px-3 py-2 text-sm w-64"
          />
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Title
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {activeTab === 'jobs' ? 'Company' : 'Client'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Posted Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentData.map((item) => (
                <tr key={item.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{item.title}</div>
                    <div className="text-sm text-gray-500">{item.location}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {item.company || item.client || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(item.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(item.postedDate)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    {(item.status || 'active') === 'active' && (
                      <button
                        onClick={() => handleStatusChange(item.id, activeTab.slice(0, -1), 'closed')}
                        className="text-yellow-600 hover:text-yellow-900"
                      >
                        Mark Filled
                      </button>
                    )}
                    {(item.status || 'active') === 'closed' && (
                      <button
                        onClick={() => handleStatusChange(item.id, activeTab.slice(0, -1), 'archived')}
                        className="text-gray-600 hover:text-gray-900"
                      >
                        Archive
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(item.id, activeTab.slice(0, -1), item.title)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {currentData.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No {activeTab} found matching your criteria.
          </div>
        )}
      </div>
    </div>
  );
};

export default PostingsDashboard;
